import axios from 'axios'
import { useQuery } from '@tanstack/react-query'

import { queryKeys } from '@/shared/hooks/queries/queryKeys'
import { getPrescriptionReading } from '../../apis/prescriptionReading'
import {
  type CompletedResponse,
  type PrescriptionReadingResponse,
  type PrescriptionReadingStatus,
  isAwaitingInputResponse,
  isCompletedResponse,
  isFailedResponse,
  isProcessingResponse,
  isSkippedResponse,
} from '../../types'

const POLL_INTERVAL_MS = 2000

// completed 時の表示データ = envelope の data そのまま（型の二重定義を避ける）。
type ReadingData = CompletedResponse['data']

export interface ParsedReading {
  status: PrescriptionReadingStatus
  data: ReadingData | null
  errorMessage: string | null
}

const parse = (raw: PrescriptionReadingResponse | unknown): ParsedReading => {
  if (isCompletedResponse(raw)) {
    return { status: 'completed', data: raw.data, errorMessage: null }
  }
  if (isProcessingResponse(raw)) {
    return { status: 'processing', data: null, errorMessage: null }
  }
  if (isAwaitingInputResponse(raw)) {
    return { status: 'awaiting_input', data: null, errorMessage: null }
  }
  if (isFailedResponse(raw)) {
    return { status: 'failed', data: null, errorMessage: raw.error.message }
  }
  if (isSkippedResponse(raw)) {
    return { status: 'skipped', data: null, errorMessage: null }
  }
  return {
    status: 'failed',
    data: null,
    errorMessage: '想定外のレスポンス形式',
  }
}

export interface UsePrescriptionReadingQueryResult {
  status: PrescriptionReadingStatus | null
  data: ReadingData | null
  errorMessage: string | null
  isLoading: boolean
}

// フォーカス画面を開くと現在状態を取得する（awaiting_input / processing / completed / …）。
// 生成は「提出」で駆動されるため GET は焚かない。processing の間だけ 2s ポーリングし、
// completed/failed/skipped/awaiting_input で自動停止する（guidance V3 useGuidanceQueryV3 踏襲）。
export const usePrescriptionReadingQuery = (
  yakurekiId: string | undefined,
  options?: { enabled?: boolean }
): UsePrescriptionReadingQueryResult => {
  const externalEnabled = options?.enabled ?? true
  const query = useQuery<ParsedReading>({
    queryKey: queryKeys.prescriptionReading.detail(yakurekiId ?? ''),
    queryFn: async () => {
      const response = await getPrescriptionReading(yakurekiId as string)
      return parse(response.data)
    },
    enabled: externalEnabled && !!yakurekiId,
    staleTime: 1000 * 60 * 30,
    refetchInterval: (q) => {
      const state = q.state.data as ParsedReading | undefined
      return state?.status === 'processing' ? POLL_INTERVAL_MS : false
    },
    // 生成を待つ間はモーダルを開いたまま別タブを見ても完了を拾えるよう背景でも継続する。
    refetchIntervalInBackground: true,
    retry: (failureCount, error) => {
      if (axios.isAxiosError(error)) {
        const code = error.response?.status
        if (code === 403 || code === 404) return false
      }
      return failureCount < 3
    },
  })

  const status = query.data?.status ?? null
  const isPolling = status === 'processing'

  return {
    status,
    data: query.data?.data ?? null,
    errorMessage: query.data?.errorMessage ?? null,
    isLoading: query.isLoading || isPolling,
  }
}
